import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { getInitials } from "@/lib/avatar-utils";
import { 
  Settings as SettingsIcon, 
  User, 
  Mail, 
  Calendar, 
  Users, 
  Upload, 
  Palette,
  Key,
  Brain,
  Building2,
  FileUp,
  FileImage,
  File,
  CheckCircle,
  Shield,
  Bell,
  Globe,
  Database,
  Zap,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  AlertTriangle,
  Info,
  LogOut,
  Sparkles,
  ImageIcon,
  Trash2,
  X
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getApiKeySource, hasOpenAIApiKey } from "@/lib/env-validator";
import { Skeleton } from "@/components/ui/skeleton";
import { QueueProcessorButton } from "@/components/admin/QueueProcessorButton";
import { getDefaultProvider, setDefaultProvider, type ProviderType } from "@/ai/providers";
import { getRecommendedModel } from "@/ai/models";
import { useAIProvider } from "@/hooks/use-ai-provider";
import { generateAIText } from "@/lib/ai-text";
import { PolicyCover } from "@/components/policy/PolicyCover";

const Settings = () => {
  const { toast } = useToast();
  const { company, profile, hasRole, refreshProfile, signOut } = useAuth();
  
  // User Profile Settings
  const [profileSettings, setProfileSettings] = useState({
    name: "",
    email: "",
    designation: "",
    avatar: null as File | null
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    password: "",
    confirmPassword: "",
    passwordExpiry: "3",
    twoFactorEnabled: false,
    sessionTimeout: "30"
  });

  // AI & Integration Settings
  const [aiSettings, setAiSettings] = useState({
    openaiApiKey: "",
    geminiApiKey: "",
    defaultProvider: "gemini" as ProviderType,
    botName: "AI Agent",
    aiEnabled: true,
    autoSuggestions: true,
    smartCompletions: true
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    policyReminders: true,
    approvalRequests: true,
    renewalAlerts: true,
    reminderFrequency: "weekly",
    leadTime: "60"
  });

  // Branding & Appearance
  const [brandingSettings, setBrandingSettings] = useState({
    primaryColor: "#6366f1",
    secondaryColor: "#10b981",
    tertiaryColor: "#f59e0b",
    logo: null as File | null,
    googleFontsLink: "",
    darkMode: false
  });

  // Company Settings (Admin only)
  const [companySettings, setCompanySettings] = useState({
    name: "",
    knowledge: "",
    uploadedDeck: null as {
      name: string;
      size: string;
      type: string;
      uploadedAt: Date;
    } | null,
    allowPdfPreview: true
  });

  // Cover state
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef<HTMLInputElement | null>(null);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isParsingKnowledge, setIsParsingKnowledge] = useState(false);
  const [loadingTipIndex, setLoadingTipIndex] = useState(0);
  const [parsingTipIndex, setParsingTipIndex] = useState(0);
  const [knowledgeWizardOpen, setKnowledgeWizardOpen] = useState(false);

  // Dynamic professional loading messages (like ChatGPT)
  const loadingTips: Array<{ text: string; icon: React.ElementType }> = [
    { text: "Syncing policy registry…", icon: File },
    { text: "Fetching company profile…", icon: Building2 },
    { text: "Warming up AI endpoints…", icon: Zap },
    { text: "Verifying permissions & roles…", icon: Shield },
    { text: "Collecting department data…", icon: Users },
    { text: "Scheduling compliance reminders…", icon: Calendar },
    { text: "Calibrating governance settings…", icon: Globe },
  ];

  // Fun but professional parsing messages for uploaded knowledge docs
  const parsingTips: Array<{ text: string; icon: React.ElementType }> = [
    { text: "Extracting slides and sections…", icon: File },
    { text: "Reading bullet points and headings…", icon: File },
    { text: "Cleaning headers and footers…", icon: File },
    { text: "Structuring knowledge for AI…", icon: Brain },
    { text: "Finalizing import…", icon: CheckCircle },
  ];

  // Cycle loading tips while isLoading
  useEffect(() => {
    if (!isLoading) return;
    const id = setInterval(() => {
      setLoadingTipIndex((i) => (i + 1) % loadingTips.length);
    }, 1200);
    return () => clearInterval(id);
  }, [isLoading]);

  // Cycle parsing tips while parsing knowledge
  useEffect(() => {
    if (!isParsingKnowledge) return;
    const id = setInterval(() => {
      setParsingTipIndex((i) => (i + 1) % parsingTips.length);
    }, 1000);
    return () => clearInterval(id);
  }, [isParsingKnowledge]);
  
  // Role check
  const isAdmin = hasRole ? hasRole('admin') : false;

  // Load saved settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      
      try {
        // Load from localStorage
        const savedApiKey = localStorage.getItem("openai_api_key");
        const savedGeminiApiKey = localStorage.getItem("gemini_api_key");
        const savedProvider = localStorage.getItem("ai_provider_preference") as ProviderType;
        const savedBotName = localStorage.getItem("bot_name");
        const savedKnowledge = localStorage.getItem("company_knowledge");
        const savedDeck = localStorage.getItem("company_deck");
        const savedNotifications = localStorage.getItem("notification_settings");
        const savedBranding = localStorage.getItem("branding_settings");
        
        // Initialize AI settings
        if (savedApiKey) {
          setAiSettings(prev => ({ ...prev, openaiApiKey: savedApiKey }));
        }
        if (savedGeminiApiKey) {
          setAiSettings(prev => ({ ...prev, geminiApiKey: savedGeminiApiKey }));
        }
        if (savedProvider && (savedProvider === 'openai' || savedProvider === 'gemini')) {
          setAiSettings(prev => ({ ...prev, defaultProvider: savedProvider }));
        } else {
          // Default to Gemini if no preference is saved
          setAiSettings(prev => ({ ...prev, defaultProvider: 'gemini' }));
        }
        if (savedBotName) {
          setAiSettings(prev => ({ ...prev, botName: savedBotName }));
        }
        
        // Initialize company settings
        if (savedKnowledge) {
          setCompanySettings(prev => ({ ...prev, knowledge: savedKnowledge }));
        }

        if (savedDeck) {
          setCompanySettings(prev => ({ ...prev, uploadedDeck: JSON.parse(savedDeck) }));
        }

        // Load notification preferences
        if (savedNotifications) {
          setNotificationSettings(JSON.parse(savedNotifications));
        }

        // Load branding preferences
        if (savedBranding) {
          setBrandingSettings(JSON.parse(savedBranding));
        }
        
        // Initialize company name from context
        if (company?.name) {
          setCompanySettings(prev => ({ ...prev, name: company.name }));
        }
        
        // Load company settings including PDF preview setting
        if (company?.id) {
          const { data: companyData, error: companyError } = await supabase
            .from('companies')
            .select('*')
            .eq('id', company.id)
            .single();
          
          if (!companyError && companyData) {
            setCompanySettings(prev => ({ 
              ...prev, 
              allowPdfPreview: (companyData as any).allow_pdf_preview !== false // Default to true if not set
            }));
          }
        }

        // Initialize profile from database
        if (profile) {
          setProfileSettings({
            name: profile.name || "",
            email: profile.email || "",
            designation: profile.designation || "",
            avatar: null
          });
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [profile, company]);

  // Save handlers for different sections
  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: profileSettings.name,
          designation: profileSettings.designation
        })
        .eq('id', profile?.id);

      if (error) throw error;
      
      toast({ title: "Profile Updated", description: "Your profile has been updated successfully." });
      await refreshProfile();
      setHasUnsavedChanges(false);
    } catch (e: any) {
      toast({ title: "Update failed", description: e?.message || 'Could not update profile', variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAllSettings = async () => {
    setIsLoading(true);
    try {
      // Save to localStorage
      localStorage.setItem("openai_api_key", aiSettings.openaiApiKey);
      localStorage.setItem("gemini_api_key", aiSettings.geminiApiKey);
      setDefaultProvider(aiSettings.defaultProvider);
      localStorage.setItem("bot_name", aiSettings.botName);
      localStorage.setItem("company_knowledge", companySettings.knowledge);
      localStorage.setItem("notification_settings", JSON.stringify(notificationSettings));
      localStorage.setItem("branding_settings", JSON.stringify(brandingSettings));
      
      if (companySettings.uploadedDeck) {
        localStorage.setItem("company_deck", JSON.stringify(companySettings.uploadedDeck));
      }
      
      toast({ title: "Settings Saved", description: "All settings have been saved successfully." });
      setHasUnsavedChanges(false);
    } catch (error) {
      toast({ title: "Save failed", description: "Could not save settings", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // Convert a File to a data URL for multimodal file message input
  const fileToDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Trigger file picker for knowledge upload
  const handleClickUploadKnowledge = () => {
    fileInputRef.current?.click();
  };

  // Handle selected PDF/PPT and parse via OpenAI Responses API, appending to knowledge
  const handleKnowledgeFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Unsupported file", description: "Please upload a PDF or PowerPoint file.", variant: "destructive" });
      event.target.value = "";
      return;
    }

    setIsParsingKnowledge(true);
    try {
      // Use default provider for parsing
      const provider = getDefaultProvider();
      const model = getRecommendedModel(provider, 'PARSING');

      // Convert to data URL for multimodal file message
      const dataUrl = await fileToDataUrl(file);

      // System instruction to extract clean, readable text suitable for knowledge base
      const system = "You are a document parser. Extract the complete readable text from the provided file. Preserve headings and bullet points. Remove headers/footers, page numbers, and artifacts. Output plain text only.";

      const messages: any[] = [
        {
          role: 'user',
          content: [
            {
              type: 'file',
              data: dataUrl,
              mediaType: file.type,
              filename: file.name
            }
          ]
        }
      ];

      const extracted = await generateAIText({
        provider,
        model,
        system,
        messages,
        temperature: 0.0
      });
      if (!extracted.trim()) {
        toast({ title: "Parsing failed", description: "No text could be extracted from the file.", variant: "destructive" });
        return;
      }

      // Append to knowledge text area
      setCompanySettings(prev => ({
        ...prev,
        knowledge: (prev.knowledge ? prev.knowledge + "\n\n" : "") + `# Imported from ${file.name}\n\n${extracted.trim()}`
      }));
      setHasUnsavedChanges(true);

      toast({ title: "Knowledge updated", description: `${file.name} parsed and added to Company Knowledge.` });
    } catch (e: any) {
      console.error('Knowledge parse error:', e);
      toast({ title: "Parse error", description: e?.message || 'Failed to parse the uploaded file.', variant: "destructive" });
    } finally {
      setIsParsingKnowledge(false);
      if (event.target) event.target.value = "";
    }
  };

  const handleChangePassword = async () => {
    if (!securitySettings.password || !securitySettings.confirmPassword) {
      toast({ title: "Missing fields", description: "Please fill in both password fields.", variant: "destructive" });
      return;
    }
    if (securitySettings.password !== securitySettings.confirmPassword) {
      toast({ title: "Password mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    if (securitySettings.password.length < 6) {
      toast({ title: "Weak password", description: "Password must be at least 6 characters long.", variant: "destructive" });
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: securitySettings.password
      });
      
      if (error) throw error;
      
      toast({ title: "Password Updated", description: "Your password has been updated successfully." });
      
      // Clear password fields
      setSecuritySettings(prev => ({
        ...prev,
        password: "",
        confirmPassword: ""
      }));
    } catch (e: any) {
      toast({ title: "Update failed", description: e?.message || 'Could not update password', variant: "destructive" });
    }
  };

  const handleCoverUpload = async (file: File) => {
    if (!profile?.company_id) return;
    setCoverUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${profile.company_id}/cover.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('company-covers')
        .upload(path, file, { contentType: file.type, upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('company-covers').getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: dbError } = await supabase
        .from('companies')
        .update({ cover_image_url: publicUrl } as any)
        .eq('id', profile.company_id);
      if (dbError) throw dbError;

      await refreshProfile();
      toast({ title: 'Cover updated', description: 'The new cover is now live on all policies.' });
    } catch (e: any) {
      toast({ title: 'Upload failed', description: e?.message ?? 'Could not upload cover.', variant: 'destructive' });
    } finally {
      setCoverUploading(false);
    }
  };

  const handleCoverRemove = async () => {
    if (!profile?.company_id) return;
    setCoverUploading(true);
    try {
      const { error: dbError } = await supabase
        .from('companies')
        .update({ cover_image_url: null } as any)
        .eq('id', profile.company_id);
      if (dbError) throw dbError;
      await refreshProfile();
      toast({ title: 'Cover removed', description: 'Policies will now show the default generated cover.' });
    } catch (e: any) {
      toast({ title: 'Remove failed', description: e?.message ?? 'Could not remove cover.', variant: 'destructive' });
    } finally {
      setCoverUploading(false);
    }
  };

  const handleSaveCompanyName = async () => {
    if (!isAdmin) {
      toast({ title: "Permission denied", description: "Only admins can change the company name.", variant: "destructive" });
      return;
    }
    if (!profile?.company_id) {
      toast({ title: "No Company", description: "No company is associated with your profile.", variant: "destructive" });
      return;
    }
    if (!companySettings.name.trim()) {
      toast({ title: "Invalid name", description: "Company name cannot be empty.", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase
        .from('companies')
        .update({ name: companySettings.name.trim() })
        .eq('id', profile.company_id);
      if (error) throw error;
      toast({ title: "Company Updated", description: "Company name updated successfully." });
      await refreshProfile();
    } catch (e: any) {
      toast({ title: "Update failed", description: e?.message || 'Could not update company name', variant: "destructive" });
    }
  };

  const handleSavePdfPreviewSetting = async (nextValue: boolean) => {
    if (!isAdmin) {
      toast({ title: "Permission denied", description: "Only admins can change this setting.", variant: "destructive" });
      return;
    }
    if (!profile?.company_id) {
      toast({ title: "No Company", description: "No company is associated with your profile.", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase
        .from('companies')
        .update({ allow_pdf_preview: nextValue } as any)
        .eq('id', profile.company_id);
      if (error) throw error;
      toast({ title: "Setting Updated", description: "PDF preview setting updated successfully." });
      await refreshProfile();
    } catch (e: any) {
      toast({ title: "Update failed", description: e?.message || 'Could not update PDF preview setting', variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Dynamic Loading Message */}
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          {(() => {
            const TipIcon = loadingTips[loadingTipIndex].icon;
            return <TipIcon className="w-4 h-4" />;
          })()}
          <span className="text-sm">{loadingTips[loadingTipIndex].text}</span>
        </div>
        {/* Header Skeleton */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <Skeleton variant="shimmer" className="h-8 w-32" />
              <Skeleton variant="shimmer" className="h-5 w-64 mt-2" />
            </div>
            <Skeleton variant="shimmer" className="h-6 w-32" />
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="space-y-6">
          <div className="flex space-x-4 overflow-x-auto">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="shimmer" className="h-10 w-24 rounded-md" />
            ))}
          </div>

          {/* Profile Tab Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Overview Skeleton */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <Skeleton variant="shimmer" className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center space-y-4">
                  <Skeleton variant="shimmer" className="w-20 h-20 rounded-full" />
                  <div className="text-center space-y-2">
                    <Skeleton variant="shimmer" className="h-5 w-32" />
                    <Skeleton variant="shimmer" className="h-4 w-24" />
                    <Skeleton variant="shimmer" className="h-3 w-40" />
                  </div>
                  <Skeleton variant="shimmer" className="h-9 w-full" />
                </div>
              </CardContent>
            </Card>

            {/* Profile Settings Skeleton */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <Skeleton variant="shimmer" className="h-6 w-40" />
                <Skeleton variant="shimmer" className="h-4 w-64" />
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton variant="shimmer" className="h-4 w-20" />
                      <Skeleton variant="shimmer" className="h-10 w-full" />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Skeleton variant="shimmer" className="h-10 w-28" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Security Settings Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton variant="shimmer" className="h-6 w-32" />
                  <Skeleton variant="shimmer" className="h-4 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="space-y-2">
                      <Skeleton variant="shimmer" className="h-4 w-24" />
                      <Skeleton variant="shimmer" className="h-10 w-full" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* AI Settings Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton variant="shimmer" className="h-6 w-32" />
                  <Skeleton variant="shimmer" className="h-4 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Skeleton variant="shimmer" className="h-4 w-24" />
                        <Skeleton variant="shimmer" className="h-3 w-40" />
                      </div>
                      <Skeleton variant="shimmer" className="h-5 w-10 rounded-full" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Global Save Button Skeleton */}
        <div className="flex justify-end">
          <Skeleton variant="shimmer" className="h-11 w-36" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account, preferences, and company settings
            </p>
          </div>
          {hasUnsavedChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-200">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Unsaved changes
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            AI & Tools
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="branding" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="company" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Company
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Overview */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profile Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center space-y-4">
                  <Avatar className="w-20 h-20">
                    <AvatarFallback name={profileSettings.name}>
                      {getInitials(profileSettings.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-center">
                    <h3 className="font-semibold">{profileSettings.name || "No name set"}</h3>
                    <p className="text-sm text-muted-foreground">{profileSettings.designation || "No designation"}</p>
                    <p className="text-xs text-muted-foreground">{profileSettings.email}</p>
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    Change Avatar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Profile Settings */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Update your personal details and profile information
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profileSettings.name}
                      onChange={(e) => setProfileSettings({...profileSettings, name: e.target.value})}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileSettings.email}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="designation">Job Title</Label>
                    <Input
                      id="designation"
                      value={profileSettings.designation}
                      onChange={(e) => setProfileSettings({...profileSettings, designation: e.target.value})}
                      placeholder="Enter your job title"
                    />
                  </div>
                </div>

                <Separator />

                <div className="flex justify-end">
                  <Button onClick={handleSaveProfile} disabled={isLoading}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Password Security */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Password Security
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Update your password and security preferences
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={securitySettings.password}
                    onChange={(e) => setSecuritySettings({...securitySettings, password: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={securitySettings.confirmPassword}
                      onChange={(e) => setSecuritySettings({...securitySettings, confirmPassword: e.target.value})}
                      className="flex-1"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleChangePassword}
                      disabled={!securitySettings.password || !securitySettings.confirmPassword}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Change
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Password Expiry</Label>
                  <Select 
                    value={securitySettings.passwordExpiry} 
                    onValueChange={(value) => setSecuritySettings({...securitySettings, passwordExpiry: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Advanced Security */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Advanced Security
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Additional security features and session management
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Two-Factor Authentication</Label>
                    <p className="text-sm text-muted-foreground">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                  <Switch
                    checked={securitySettings.twoFactorEnabled}
                    onCheckedChange={(checked) => setSecuritySettings({...securitySettings, twoFactorEnabled: checked})}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Session Timeout</Label>
                  <Select 
                    value={securitySettings.sessionTimeout} 
                    onValueChange={(value) => setSecuritySettings({...securitySettings, sessionTimeout: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="480">8 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {isAdmin && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label>Allow Team Members to View and Download PDF of the Policies</Label>
                        <p className="text-sm text-muted-foreground">
                          Controls the visibility of the Preview button in PolicyView for all team members
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={companySettings.allowPdfPreview}
                          onCheckedChange={(checked) => {
                            setCompanySettings({...companySettings, allowPdfPreview: checked});
                            handleSavePdfPreviewSetting(checked);
                          }}
                        />
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium">Security Tips</p>
                      <p className="text-muted-foreground">
                        Use a strong, unique password and enable two-factor authentication for better security.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Logout Section */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium mb-1">Session Management</h3>
                    <p className="text-sm text-muted-foreground">
                      Log out of your account to end your current session
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={signOut}
                    className="w-full"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Preferences
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Control how and when you receive notifications
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                    </div>
                    <Switch
                      checked={notificationSettings.emailNotifications}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, emailNotifications: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive browser push notifications</p>
                    </div>
                    <Switch
                      checked={notificationSettings.pushNotifications}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, pushNotifications: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Policy Reminders</Label>
                      <p className="text-sm text-muted-foreground">Get reminded about policy deadlines</p>
                    </div>
                    <Switch
                      checked={notificationSettings.policyReminders}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, policyReminders: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Approval Requests</Label>
                      <p className="text-sm text-muted-foreground">Notify when approval is needed</p>
                    </div>
                    <Switch
                      checked={notificationSettings.approvalRequests}
                      onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, approvalRequests: checked})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Reminder Settings
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Configure reminder frequency and timing
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Reminder Frequency</Label>
                  <Select 
                    value={notificationSettings.reminderFrequency} 
                    onValueChange={(value) => setNotificationSettings({...notificationSettings, reminderFrequency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Lead Time (Days)</Label>
                  <Select 
                    value={notificationSettings.leadTime} 
                    onValueChange={(value) => setNotificationSettings({...notificationSettings, leadTime: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 Days</SelectItem>
                      <SelectItem value="60">60 Days</SelectItem>
                      <SelectItem value="90">90 Days</SelectItem>
                      <SelectItem value="120">120 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI & Tools Tab */}
        <TabsContent value="ai">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  AI Configuration
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Configure AI-powered features and integrations
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="botName">AI Bot Name</Label>
                  <p className="text-sm text-muted-foreground">
                    Customize the name of your AI agent
                  </p>
                  <Input
                    id="botName"
                    type="text"
                    placeholder="AI Agent"
                    value={aiSettings.botName}
                    onChange={(e) => setAiSettings({...aiSettings, botName: e.target.value})}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="openaiApiKey">Bring Your Own Key (BYOK)</Label>
                    {getApiKeySource() === 'byok' && (
                      <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getApiKeySource() === 'byok'
                      ? "✅ Using your personal OpenAI API key. Your key stays secure in your browser."
                      : "No API key configured. Enter your OpenAI API key below to enable AI features."}
                  </p>
                  <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium">How to get your API key:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">platform.openai.com/api-keys</a></li>
                          <li>Click "Create new secret key"</li>
                          <li>Copy the key (starts with <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">sk-</code>)</li>
                          <li>Paste it below and click Save</li>
                        </ol>
                        <p className="mt-2 text-muted-foreground italic">
                          ⚠️ Use an API Key (sk-...), NOT an Organization ID (k-proj-...)
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="openaiApiKey"
                      type="password"
                      placeholder="sk-..."
                      value={aiSettings.openaiApiKey}
                      onChange={(e) => setAiSettings({...aiSettings, openaiApiKey: e.target.value})}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const key = aiSettings.openaiApiKey.trim();
                        
                        // Validate key format
                        if (key && key.startsWith('k-proj-')) {
                          toast({
                            title: "Invalid API Key",
                            description: "You entered an Organization ID. Please use an API Key that starts with 'sk-'",
                            variant: "destructive"
                          });
                          return;
                        }
                        
                        if (key && !key.startsWith('sk-')) {
                          toast({
                            title: "Invalid API Key",
                            description: "API keys must start with 'sk-'. Get your key at platform.openai.com/api-keys",
                            variant: "destructive"
                          });
                          return;
                        }
                        
                        localStorage.setItem("openai_api_key", key);
                        toast({
                          title: "API Key Saved",
                          description: "Your OpenAI API key (BYOK) has been saved successfully.",
                        });
                        
                        // Clear the input
                        setAiSettings({...aiSettings, openaiApiKey: ""});
                      }}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="geminiApiKey">Google Gemini API Key (Optional)</Label>
                    {localStorage.getItem("gemini_api_key") && (
                      <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Configure Gemini API key to use Google's AI models as an alternative to OpenAI.
                  </p>
                  <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium">How to get your Gemini API key:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Visit <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">aistudio.google.com/app/apikey</a></li>
                          <li>Click "Create API Key"</li>
                          <li>Copy the key</li>
                          <li>Paste it below and click Save</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="geminiApiKey"
                      type="password"
                      placeholder="Enter Gemini API key..."
                      value={aiSettings.geminiApiKey}
                      onChange={(e) => setAiSettings({...aiSettings, geminiApiKey: e.target.value})}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const key = aiSettings.geminiApiKey.trim();
                        
                        if (key && key.length < 20) {
                          toast({
                            title: "Invalid API Key",
                            description: "Gemini API keys should be longer. Get your key at aistudio.google.com/app/apikey",
                            variant: "destructive"
                          });
                          return;
                        }
                        
                        localStorage.setItem("gemini_api_key", key);
                        toast({
                          title: "API Key Saved",
                          description: "Your Gemini API key has been saved successfully.",
                        });
                        
                        setAiSettings({...aiSettings, geminiApiKey: ""});
                      }}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="defaultProvider">Default AI Provider</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose which AI provider to use by default for AI features.
                  </p>
                  <Select
                    value={aiSettings.defaultProvider}
                    onValueChange={(value) => {
                      const provider = value as ProviderType;
                      setAiSettings({...aiSettings, defaultProvider: provider});
                      setDefaultProvider(provider);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai" disabled={!localStorage.getItem("openai_api_key")}>
                        OpenAI {!localStorage.getItem("openai_api_key") && "(API key required)"}
                      </SelectItem>
                      <SelectItem value="gemini" disabled={!localStorage.getItem("gemini_api_key")}>
                        Gemini {!localStorage.getItem("gemini_api_key") && "(API key required)"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {aiSettings.defaultProvider === 'openai' 
                      ? 'Using OpenAI for AI features. Configure Gemini API key to enable Gemini as an option.'
                      : 'Using Gemini for AI features. Configure OpenAI API key to enable OpenAI as an option.'}
                  </p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>AI Features</Label>
                      <p className="text-sm text-muted-foreground">Enable AI-powered features</p>
                    </div>
                    <Switch
                      checked={aiSettings.aiEnabled}
                      onCheckedChange={(checked) => setAiSettings({...aiSettings, aiEnabled: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Auto Suggestions</Label>
                      <p className="text-sm text-muted-foreground">Get AI-powered suggestions</p>
                    </div>
                    <Switch
                      checked={aiSettings.autoSuggestions}
                      onCheckedChange={(checked) => setAiSettings({...aiSettings, autoSuggestions: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Smart Completions</Label>
                      <p className="text-sm text-muted-foreground">AI-powered text completions</p>
                    </div>
                    <Switch
                      checked={aiSettings.smartCompletions}
                      onCheckedChange={(checked) => setAiSettings({...aiSettings, smartCompletions: checked})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  AI Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${hasOpenAIApiKey() ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm font-medium">API Connection</span>
                    </div>
                    <Badge variant={hasOpenAIApiKey() ? "default" : "destructive"}>
                      {hasOpenAIApiKey() ? "Connected" : "Not Connected"}
                    </Badge>
                  </div>
                  
                  {hasOpenAIApiKey() && (
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">AI Source</span>
                      </div>
                      <Badge variant={getApiKeySource() === 'manageaze' ? "default" : "secondary"}>
                        {getApiKeySource() === 'manageaze' ? "Manageaze AI" : "BYOK"}
                      </Badge>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${aiSettings.aiEnabled ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <span className="text-sm font-medium">AI Features</span>
                    </div>
                    <Badge variant={aiSettings.aiEnabled ? "default" : "secondary"}>
                      {aiSettings.aiEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">AI Features</p>
                      <p className="text-blue-700 dark:text-blue-200">
                        AI features help you create better policies with smart suggestions and automated completions.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RAG Queue Management (Admin Only) */}
          {isAdmin && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" />
                RAG Queue Management
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Process pending policy embeddings for the AI search system. This enables semantic search of approved policies.
              </p>
              <QueueProcessorButton />
            </div>
          )}
        </TabsContent>

        {/* Admin-only tabs */}
        {isAdmin && (
          <>
            {/* Branding Tab */}
            <TabsContent value="branding">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="w-5 h-5" />
                      Brand Colors
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Customize your company's brand colors
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Label className="w-24">Primary Color</Label>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-10 h-8 rounded border border-border"
                            style={{ backgroundColor: brandingSettings.primaryColor }}
                          />
                          <Input
                            type="color"
                            value={brandingSettings.primaryColor}
                            onChange={(e) => setBrandingSettings({...brandingSettings, primaryColor: e.target.value})}
                            className="w-20 h-8 p-1"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Label className="w-24">Secondary Color</Label>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-10 h-8 rounded border border-border"
                            style={{ backgroundColor: brandingSettings.secondaryColor }}
                          />
                          <Input
                            type="color"
                            value={brandingSettings.secondaryColor}
                            onChange={(e) => setBrandingSettings({...brandingSettings, secondaryColor: e.target.value})}
                            className="w-20 h-8 p-1"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Label className="w-24">Accent Color</Label>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-10 h-8 rounded border border-border"
                            style={{ backgroundColor: brandingSettings.tertiaryColor }}
                          />
                          <Input
                            type="color"
                            value={brandingSettings.tertiaryColor}
                            onChange={(e) => setBrandingSettings({...brandingSettings, tertiaryColor: e.target.value})}
                            className="w-20 h-8 p-1"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Brand Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div 
                        className="h-32 rounded-lg flex items-center justify-center"
                        style={{ 
                          background: `linear-gradient(135deg, ${brandingSettings.primaryColor}, ${brandingSettings.secondaryColor})` 
                        }}
                      >
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: brandingSettings.tertiaryColor }}
                        >
                          <SettingsIcon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="bg-muted/50 h-24 rounded-lg flex items-center justify-center">
                        <p className="text-muted-foreground">Brand preview</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Company Tab */}
            <TabsContent value="company">
              <div className="space-y-6">
                {/* Company Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Company Information
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Manage your company details and knowledge base
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="companyName"
                          value={companySettings.name}
                          onChange={(e) => setCompanySettings({...companySettings, name: e.target.value})}
                          className="flex-1"
                        />
                        <Button onClick={handleSaveCompanyName}>
                          Save
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                          <Database className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Company Knowledge Base has moved</p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            Upload regulations, compliance documents, and your company profile in the dedicated Knowledge Base — a first-class feature the AI policy agent draws from automatically.
                          </p>
                        </div>
                      </div>
                      <a href="/knowledge-base">
                        <Button size="sm" className="gap-2 mt-1">
                          <Database className="h-4 w-4" />
                          Go to Knowledge Base
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Policy Cover */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ImageIcon className="w-5 h-5" />
                      Policy Cover
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      This cover appears at the top of every policy page. If none is set, a branded pattern is generated automatically.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Preview */}
                    <div className="overflow-hidden rounded-lg border" style={{ height: 128 }}>
                      <div className="scale-[0.667] origin-top-left" style={{ width: '150%', height: '150%' }}>
                        <PolicyCover coverUrl={company?.cover_image_url} companyName={company?.name ?? ''} />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        ref={coverInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleCoverUpload(file);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={coverUploading || !isAdmin}
                        onClick={() => coverInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-1.5" />
                        {coverUploading ? 'Uploading…' : company?.cover_image_url ? 'Change Cover' : 'Upload Cover'}
                      </Button>
                      {company?.cover_image_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={coverUploading || !isAdmin}
                          onClick={handleCoverRemove}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1.5" />
                          Remove
                        </Button>
                      )}
                    </div>
                    {!isAdmin && (
                      <p className="text-xs text-muted-foreground">Only admins can change the policy cover.</p>
                    )}
                  </CardContent>
                </Card>

              </div>
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Global Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveAllSettings} disabled={isLoading} size="lg">
          <Save className="w-4 h-4 mr-2" />
          Save All Settings
        </Button>
      </div>

    </div>
  );
};

export default Settings;